import { DeleteOutlined, TranslationOutlined, PlusOutlined } from '@ant-design/icons';
import { Input, Button, Space } from 'antd';
import { type TextAreaProps } from 'antd/es/input';
import { useField, FieldArray } from 'formik';
import { memo } from 'react';
import styled from 'styled-components';
import { type ContentRefinementProps, useRefineContentButton } from '../../button/RefineContentButton';
import FormItem, { type FormItemProps } from '../FormItem';

export interface TextAreaFieldArrayProps
    extends Omit<TextAreaProps, 'value' | 'onChange' | 'autoComplete'>, ContentRefinementProps {
    name: string;
    label?: string;
    itemProps?: Omit<FormItemProps, 'label' | 'meta' | 'children'>;
    isDefault?: boolean;
    openTranslationDrawer?: (id: number) => void;
    onDelete?: () => void; // optional because we don't need ondelete for default value
    labelForAdd?: string;
    limit?: number;
    showAdd?: boolean;
}

const StyledDiv = styled(Input.Group)`
    display: flex;

    button {
        margin: auto 10px;
        height: 100%;
    }

    div:last-child {
        border-right: 1px solid #d9d9d9;
    }
`;

const StyledButtonContainer = styled.div`
    background: #fafafa;
    border-top: 1px solid #d9d9d9;
    border-bottom: 1px solid #d9d9d9;
`;

const TextAreaFieldArray = ({
    name,
    required,
    label,
    isDefault = false,
    openTranslationDrawer,
    onDelete,
    itemProps,
    limit,
    labelForAdd,
    showAdd = false,
    withContentRefinement,
    ...props
}: TextAreaFieldArrayProps) => {
    const [field, meta, helpers] = useField({ name });
    const { value } = meta;

    const refineContentButton = useRefineContentButton();

    return (
        <FormItem {...itemProps} label={label} meta={meta} required={required}>
            <FieldArray
                {...field}
                name={name}
                render={arrayHelpers => (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {value.map((priceDisclaimer, index) => (
                            <div>
                                <StyledDiv>
                                    <Input.TextArea
                                        key={`${name}.${index.toString()}`}
                                        autoComplete="off"
                                        // spread props
                                        {...props}
                                        // then spread the field properties itself
                                        {...field}
                                        onBlur={props.onBlur || field.onBlur}
                                        onChange={e => {
                                            const newValue = value.map((data, i) => {
                                                if (index === i) {
                                                    return {
                                                        ...data,
                                                        defaultValue: e.target.value,
                                                    };
                                                }

                                                return data;
                                            });

                                            helpers.setValue(newValue);
                                        }}
                                        value={priceDisclaimer.defaultValue}
                                    />
                                    {props.disabled ? null : (
                                        <StyledButtonContainer>
                                            <Button
                                                icon={
                                                    <TranslationOutlined
                                                        style={{ color: 'var(--ant-primary-color)' }}
                                                    />
                                                }
                                                onClick={e => openTranslationDrawer(index)}
                                                size="small"
                                                style={{ height: '100%' }}
                                                type="text"
                                            />
                                        </StyledButtonContainer>
                                    )}
                                    {/* we won't show delete button on first entry of default value */}
                                    {(!isDefault || (isDefault && index !== 0)) && (
                                        <StyledButtonContainer>
                                            <Button
                                                icon={<DeleteOutlined style={{ color: 'var(--ant-primary-color)' }} />}
                                                onClick={() => {
                                                    arrayHelpers.remove(index);
                                                }}
                                                size="small"
                                                style={{ height: '100%' }}
                                                type="text"
                                            />
                                        </StyledButtonContainer>
                                    )}
                                </StyledDiv>
                                {withContentRefinement && (
                                    <div style={{ marginBottom: '10px' }}>
                                        {refineContentButton.render({ name, index })}
                                    </div>
                                )}
                            </div>
                        ))}
                        {(limit && value.length >= limit - 1) || !showAdd ? null : (
                            <Button
                                icon={<PlusOutlined />}
                                onClick={() => {
                                    arrayHelpers.push({ defaultValue: '', overrides: [] });
                                }}
                                type="link"
                            >
                                {labelForAdd}
                            </Button>
                        )}
                    </Space>
                )}
            />
        </FormItem>
    );
};

export default memo(TextAreaFieldArray);
